import React, { useState, useEffect } from "react";
import { useHotel } from "../contexts/HotelContext";
import { getHotelSettings, updateHotelSettings } from "../services/hotelApi";
import Layout from "../components/Layout";

const mapApiErrorMessage = (message: string | undefined): string => {
  if (!message) return "Wystąpił błąd podczas zapisywania ustawień";
  if (message.includes("must be shorter than or equal to 2000")) {
    return "Uwagi są za długie (maksymalnie 2000 znaków)";
  }
  return "Wystąpił błąd podczas zapisywania ustawień";
};

const HotelSettings: React.FC = () => {
  const { user } = useHotel();
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Settings state
  const [breakfastPrice, setBreakfastPrice] = useState<number>(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");

  // Default notes state
  const [checkInNotes, setCheckInNotes] = useState<string>("");
  const [checkOutNotes, setCheckOutNotes] = useState<string>("");

  // Get hotel ID from user's default hotel or first hotel
  const hotelId = user?.defaultHotel?.id || user?.hotels?.[0]?.id;

  useEffect(() => {
    const fetchSettings = async () => {
      if (!hotelId) return;

      try {
        const settings = await getHotelSettings(hotelId);
        setBreakfastPrice(settings.breakfastNetPricePerPerson || 0);
        setCheckInNotes(settings.checkInNotes || "");
        setCheckOutNotes(settings.checkOutNotes || "");
        setFetchError(null);
      } catch {
        setFetchError(
          "Nie udało się pobrać ustawień hotelu. Odśwież stronę, aby spróbować ponownie."
        );
      } finally {
        setLoading(false);
      }
    };

    if (hotelId) {
      fetchSettings();
    } else {
      setLoading(false);
    }
  }, [hotelId]);

  const handleSaveSettings = async () => {
    if (!hotelId) return;

    setIsSaving(true);
    setSaveMessage("");

    try {
      await updateHotelSettings(hotelId, {
        breakfastNetPricePerPerson: breakfastPrice,
        checkInNotes: checkInNotes.trim() || null,
        checkOutNotes: checkOutNotes.trim() || null,
      });
      setSaveMessage("Ustawienia zapisane!");
      setTimeout(() => setSaveMessage(""), 3000);
    } catch (error: any) {
      setSaveMessage(mapApiErrorMessage(error.response?.data?.message));
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout showBackButton={true}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-neutral-600">Ładowanie...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!hotelId) {
    return (
      <Layout showBackButton={true}>
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
            <div className="text-center">
              <svg
                className="h-12 w-12 text-neutral-400 mx-auto mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m0 0v2m0-2h2m-2 0H10m9-9a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h2 className="text-xl font-semibold text-neutral-900 mb-2">
                Brak dostępu do hotelu
              </h2>
              <p className="text-neutral-600">
                Nie masz dostępu do żadnego hotelu.
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showBackButton={true}>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Settings Section */}
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-neutral-900 mb-2">
                Ustawienia hotelu
              </h2>
              <p className="text-sm text-neutral-600">
                Konfiguracja ustawień hotelu
              </p>
            </div>

            {fetchError && (
              <div className="mb-4 p-3 rounded-lg text-sm font-medium bg-error-50 text-error-700 border border-error-200">
                {fetchError}
              </div>
            )}

            {saveMessage && (
              <div
                aria-live="polite"
                className={`mb-4 p-3 rounded-lg text-sm font-medium ${
                  saveMessage.includes("zapisane")
                    ? "bg-success-50 text-success-700 border border-success-200"
                    : "bg-error-50 text-error-700 border border-error-200"
                }`}
              >
                {saveMessage}
              </div>
            )}

            {/* Default Notes Section */}
            <div className="mb-8">
              <h3 className="text-base font-medium text-neutral-900 mb-1">
                Domyślne uwagi
              </h3>
              <p className="text-sm text-neutral-500 mb-4">
                Uwagi dodawane automatycznie do nowych ofert
              </p>

              <div className="max-w-xl space-y-4">
                <div>
                  <label
                    htmlFor="checkInNotes"
                    className="block text-sm font-medium text-neutral-700 mb-1"
                  >
                    Uwagi do check-in
                  </label>
                  <textarea
                    id="checkInNotes"
                    value={checkInNotes}
                    onChange={(e) => setCheckInNotes(e.target.value)}
                    maxLength={2000}
                    rows={4}
                    aria-describedby="checkInNotesCount"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-vertical"
                    placeholder="np. Recepcja czynna 24h. Parking dostępny od 14:00."
                  />
                  <span
                    id="checkInNotesCount"
                    className={`block text-xs mt-1 ${
                      checkInNotes.length > 1900
                        ? "text-error-600"
                        : "text-neutral-400"
                    }`}
                  >
                    {checkInNotes.length}/2000
                  </span>
                </div>

                <div>
                  <label
                    htmlFor="checkOutNotes"
                    className="block text-sm font-medium text-neutral-700 mb-1"
                  >
                    Uwagi do check-out
                  </label>
                  <textarea
                    id="checkOutNotes"
                    value={checkOutNotes}
                    onChange={(e) => setCheckOutNotes(e.target.value)}
                    maxLength={2000}
                    rows={4}
                    aria-describedby="checkOutNotesCount"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-vertical"
                    placeholder="np. Wymeldowanie do godziny 11:00."
                  />
                  <span
                    id="checkOutNotesCount"
                    className={`block text-xs mt-1 ${
                      checkOutNotes.length > 1900
                        ? "text-error-600"
                        : "text-neutral-400"
                    }`}
                  >
                    {checkOutNotes.length}/2000
                  </span>
                </div>

                <p className="text-sm text-neutral-500">
                  Uwagi będą stosowane tylko do nowych ofert. Istniejące oferty
                  nie zostaną zaktualizowane.
                </p>
              </div>
            </div>

            {/* Breakfast Price Section */}
            <div className="max-w-md">
              <label
                htmlFor="breakfastPrice"
                className="block text-sm font-medium text-neutral-700 mb-2"
              >
                Cena netto śniadania za osobę (PLN)
                <span className="block text-xs text-neutral-500 font-normal mt-1">
                  Używana do obliczania cen pokoi przy niższym obłożeniu, gdy
                  dane nie są dostępne w ProfitRoom
                </span>
              </label>
              <div className="flex items-center space-x-3">
                <input
                  id="breakfastPrice"
                  type="number"
                  min="0"
                  step="0.01"
                  value={breakfastPrice}
                  onChange={(e) =>
                    setBreakfastPrice(parseFloat(e.target.value) || 0)
                  }
                  className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Save button */}
            <div className="mt-6">
              <button
                onClick={handleSaveSettings}
                disabled={isSaving}
                className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2 px-4 rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50"
              >
                {isSaving ? "Zapisywanie..." : "Zapisz"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HotelSettings;
